import { AfterContentChecked, ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { GridApi, GridOptions } from 'ag-grid-community';
import { ReportDisplayType, ReportGridService } from '@app/shared/report/report-grid.service';
import { IColumnMetaData, IReportAction, IReportData, IReportLayoutSetting, IReportMetadata, IReportResponse, IReportRow, ReportDataService } from '@app/shared/report/report-data.service';
import _ from 'underscore';
import { util } from '@app/shared/util';
import { StateObject, StateService, Transition, UiOnParamsChanged } from '@uirouter/angular';
import { IExtendedWindow, IObject } from '@type_definitions/datatypes';
import { AuthService } from '@app/shared/services/auth/auth.service';
import { BodyClassService } from '@app/shared/services/body-class.service';
import { ScrollService } from '@app/shared/scroll/scroll.service';
import { GaMessageService } from '@app/shared/message/ga-message.service';
import { TranslationService } from '@app/shared/translation/translation.service';
import { IStoredReportChangeEvent, IStoredReportDialogStateChangeEvent } from './stored-report/stored-report.component';
import { ReportFiltersUtil } from '@app/shared/report/report-filters-util.service';
import { BrowseListService } from '@app/shared/browselists/browselists.service';
import { BrowseListConfigService, IBrowselistActionPermission, IBrowseListConfig } from '@app/shared/browselists/browselists-config.service';
import { GaDeferredService, IGADeferred } from '@app/shared/services/ga-deferred.service';
import { ReportExportService } from '@app/shared/report/export/report-export.service';
import { IStoredReport, IStoredReportColumn, StoredReportDataService } from '@app/shared/report/stored-report-data.service';
import { ReportActionsService } from '@app/shared/report/actions/report-actions.service';
import { SendMailService } from '@app/shared/send-mail/send-mail.service';
import { IMailRequest } from '@app/shared/send-mail/send-mail-data.service';
import { ACSL_COMMENT_KEY, CUSTOM_MENU_TEXT_ACSL_OTHERS, IEditableTextOption, OTHERS_COMMENT_KEY } from '@app/shared/editable-text/editable-text.service';
import { aggregateModels, IReportTableSettingsModel, OnResetLayoutSettingsDoneCallback } from './report-table-settings.component';
import { Subscription } from 'rxjs';
import { ReportLayoutSettingsService } from '@app/shared/report/report-layout-settings.service';
import { ReportParameterCode, ReportParameterShortCode } from '@app/main/report/report-state.util';

interface IReportFiltersControlModel {
  showfilters?:boolean;
  showcomments?:boolean;
  hascomments?:boolean;
}

interface IReportColumnControlModel {
  possibleColumns?:IColumnMetaData[];
}

export interface IReportActionModel {
  selectedAction?:IReportAction;
  actions?:IReportAction[];
}

export interface IStoredReportBusy {
  loading?:boolean;
  saving?:boolean;
  deleting?:boolean;
}

export const STORED_REPORT_ADD_MODE:string = 'add';
export const STORED_REPORT_EDIT_MODE:string = 'edit';

@Component({
  selector: 'report-overview',
  template: `
    <h2 *ngIf="reportStarting">
      <activity-indicator class="no-margin" [useDefaultText]="true"></activity-indicator>
    </h2>

    <!-- Report header and stored report selection -->
    <div [hidden]="reportStarting || !reportId">
      <div class="report-header clearfix">
        <div class="row">
          <div class="col-md-9 col-sm-8">
            <h2 class="report-title" [title]="'Report ' + reportId">
              {{ title }}
              <info-link *ngIf="filtersControlModel.hascomments" [(linkVariable)]="filtersControlModel.showcomments"></info-link>
            </h2>
          </div>
          <div *ngIf="isACSLUser" class="col-md-3 col-sm-4">
            <div class="float-end block-ga1-link" *ngIf="ga1ReportUrl">
              <a uiSref="main.params" [uiParams]="{ url: ga1ReportUrl }" translate>REPORTS.CLASSIC_REPORT_LAYOUT</a>
            </div>
          </div>
        </div>

        <!-- We define screen, because currently translation implementation does not allow number at the end of route -->
        <text [customMenu]="reportCommentCustomMenu" [key]="reportCommentKey" [screen]="reportCommentScreen" *ngIf="filtersControlModel.showcomments" class="report-comments"></text>
      </div>

      <div class="report-header-section report-header-section-stored-reports clearfix">
        <stored-report
          *ngIf="storedReports.length"
          class="report-header-section-stored-reports"
          mode="${STORED_REPORT_EDIT_MODE}"
          [reportId]="reportId"
          [currentFilters]="filters"
          [storedReportId]="storedReport"
          [metadata]="currentReportMetadata"
          (onChange)="selectStoredReport($event)"
          (onSave)="saveStoredReport($event)"
          (onDelete)="deleteStoredReport($event)"
          (onDialogStateChange)="onStoredReportDialogStateChange($event)"
          [storedReports]="storedReports"
          [busy]="storedReportsState">
        </stored-report>
      </div>

      <!-- Report filters and save stored report -->
      <div class="report-header-section report-header-section-filters">
        <collapsible-pane [collapsed]="!filtersControlModel.showfilters" title="Search Filters" (onChange)="filtersControlModel.showfilters = !$event;">
          <div>
            <div class="search-button-row-1">
              <ng-container *ngTemplateOutlet="searchButtonField"></ng-container>
            </div>
            <report-filters [reportMetadata]="currentReportMetadata" [model]="filters" (onSubmit)="load()" (onFilterChange)="onFilterChange($event)" [showFilters]="filtersControlModel.showfilters" *ngIf="showReportFiltersWhenDialogOpen" class="clearfix"></report-filters>
            <stored-report
              class="save-stored-reports"
              mode="${STORED_REPORT_ADD_MODE}"
              buttonLabel="{{'REPORTS.BUTTONS.SAVE_STORED_REPORT' | translate}}"
              [reportId]="reportId"
              [currentFilters]="filters"
              [metadata]="currentReportMetadata"
              (onSave)="saveStoredReport($event)"
              (onDialogStateChange)="onStoredReportDialogStateChange($event)"
              [busy]="storedReportsState">
            </stored-report>
          </div>
        </collapsible-pane>
      </div>

      <!-- Table columns -->
      <div class="report-header-section report-header-section-columns margin-bottom-sm-2">
        <collapsible-pane [collapsed]="true" title="{{'REPORTS.TABLE_COLUMNS' | translate}}">
          <select-report-columns [columns]="columnControlModel.possibleColumns" (onSelectValue)="onColumnSelection()" columnKey="formatting.defaultinlist"></select-report-columns>
        </collapsible-pane>
      </div>

      <span scrollTarget="report-results" scrollPaddingTop="15"></span>

      <!-- Report actions when have custom action -->
      <div class="row search-button-row-2" *ngIf="hasCustomActions">
        <div class="col-md-12">
          <ng-container *ngTemplateOutlet="searchButtonField"></ng-container>
        </div>
      </div>

      <!-- Report grid actions -->
      <div class="row actions-row">
        <div class="search-button-row-3 col-auto" *ngIf="!hasCustomActions" [class.col-12]="browselistConfig && hasCustomActions">
          <ng-container *ngTemplateOutlet="searchButtonField"></ng-container>
        </div>

        <div class="report-header-section-browselist" [ngClass]="{ 'col-12': hasCustomActions && browselistConfig, 'col-auto': !hasCustomActions }" *ngIf="hasCustomActions || browselistConfig">
          <span class="actions">
            <div class="browse-list-actions form-horizontal">
              <button class="btn btn-primary col-md-auto" (click)="addRow()" *ngIf="browselistConfig && browselistActionPermissions.add" translate [translateParams]="{ label: browselistConfig.label || '' }">GA.BUTTONS.NEW_LABEL</button>
              <button class="btn btn-danger col-md-auto" (click)="deleteRow(undefined, title)" *ngIf="browselistConfig && browselistActionPermissions.delete" [busyButton]="browselistDeleteBusy">
                <span translate><i class="fa fa-trash-o"></i> GA.BUTTONS.DELETE</span>
              </button>
            </div>
          </span>
        </div>

        <div class="report-header-section-actions col-auto ps-3">
          <report-actions *ngIf="hasCustomActions" [actions]="actionModel?.actions" (onAction)="executeAction($event)" [disabled]="reportDataLoading" [busy]="reportActionBusy"></report-actions>
        </div>

        <div class="report-header-section report-header-section-layout col ms-auto me-0">
          <report-table-settings [(model)]="tableSettingsModel" [reportId]="reportId" (onResetLayoutSettings)="onResetLayoutSettings($event)"></report-table-settings>

          <div class="report-operations">
            <button class="btn" (click)="exportReport()" translate>GA.BUTTONS.EXPORT</button>
            <button class="btn" (click)="sendReport()" translate [translateParams]="{ label: '' }">GA.BUTTONS.SEND_LABEL</button>
          </div>

          <div [hidden]="!rowCount" class="rowcount float-end hidden-xxs" translate [translateParams]="{ count: rowCount }">REPORTS.ROW_RESULTS</div>
        </div>
      </div>

      <!-- Report ag-grid -->
      <ga-ag-grid [gridId]="reportId" [height]="400" [gridOptions]="gridOptions" fillHeight="45px;.search-button-row-2;.actions-row;.ui-layout-north;.ui-layout-resizer-north" [gridInit]="gridInit"></ga-ag-grid>

      <!-- Report SQL -->
      <report-sql [sqlModel]="sqlModel" *ngIf="isACSLUser"></report-sql>
    </div>

    <ng-template #searchButtonField>
      <button class="btn btn-primary" [busyButton]="reportDataLoading" (click)="load()" [disabled]="reportActionBusy" translate>GA.BUTTONS.SEARCH</button>
      <button class="btn btn-danger cancel-button" *ngIf="reportDataLoading" (click)="cancelLoad()" translate>GA.BUTTONS.CANCEL</button>
    </ng-template>
  `,
  styleUrls: [ './report.scss' ],
  encapsulation: ViewEncapsulation.None
})
export class ReportComponent implements OnInit, OnDestroy, AfterContentChecked, UiOnParamsChanged {
  @Input() public showFilters:boolean;
  @Input() public showComments:boolean;
  @Input() public autoload:boolean;
  @Input() public browselistCode:string;
  @Input() public reportId:string|number;
  @Input() public formContext:string;
  @Input() public templateCode:string;
  @Input() public storedReport:string|number;
  @Input() public title:string;
  @Input() public layoutSettingToken:string;
  @Input() public menuId:number;

  public reportDataLoading:boolean;
  public reportActionBusy:boolean;
  public reportStarting:boolean;
  public browselistDeleteBusy:boolean;
  public storedReportsState:IObject = {
    loading: false,
    deleting: false,
    saving: false
  };
  public comments:string;
  public gridInit:boolean = false;
  public gridOptions:GridOptions = {};
  public filters:IObject = {};
  public filtersControlModel:IReportFiltersControlModel = {};
  public tableSettingsModel:IReportTableSettingsModel = this.defaultTableSettingsModel;
  public columnControlModel:IReportColumnControlModel = {
    possibleColumns: []
  };
  public rowCount:number = 0;
  public actionModel:IReportActionModel = {
    selectedAction: undefined,
    actions: []
  };
  public browselistActionPermissions:IBrowselistActionPermission = {};
  public hasCustomActions:boolean = false;
  public browselistConfig:IBrowseListConfig;
  public ga1ReportUrl:string;
  /**
   * @description Holds the current report metadata, reflecting the latest changes made to the grid.
   */
  public currentReportMetadata: IReportMetadata;
  public storedReports:IStoredReport[] = [];
  public showReportFiltersWhenDialogOpen:boolean = true;
  public reportCommentKey:string;
  public reportCommentScreen:string;
  public reportCommentCustomMenu:IEditableTextOption[] = CUSTOM_MENU_TEXT_ACSL_OTHERS;
  public isACSLUser:boolean;
  public sqlModel:string;
  public selectedLayoutSetting:IReportLayoutSetting;
  /**
   * @description Stores the processed filter values. If a filter value matches the default, it is set as undefined;
   * otherwise, the latest filter value is stored. This is used to maintain up-to-date filter values for
   * comparison during searches or state parameter updates.
   */
  public processedFilterValues:IObject = {};

  /**
   * @description Stores the original report metadata fetched from the server, this will be used when layout revert to original setting
   */
  private originalReportMetadata:IReportMetadata;

  /**
   * @description Stores the backup of current report metadata 
   * this is used to compare metadata when layout settings change.
   */
  private backupCurrentReportMetadata:IReportMetadata;

  /**
   * @description Stores the original stored report metadata. This is updated whenever
   * a stored report is selected using the `selectStoredReport` method. This will be used to compare
   * between current report metadata and original stored report metadata when layout settings detect changes
   */
  private originalStoredReportMetadata:IReportMetadata;

  /**
   * @description It reflects the raw or unprocessed version of the current state parameters
   * it refers to previously saved/persisted values, these are from current or a past state.
   */
  private persistedStateParams:IObject;
  private overwriteExportCheckboxes:boolean;
  private defaultFilters:IObject = {};
  private browselistContext:IObject = {};
  private layoutGridCode:string;
  private resetLayoutSettingsBusy:boolean;

  private getRowCountSubscription:Subscription;
  private onLayoutSettingChangeSubscription:Subscription;
  private onLayoutSettingSavedSubscription:Subscription;

  constructor(
    private authService:AuthService,
    private browseListConfigService:BrowseListConfigService,
    private bodyClassService:BodyClassService,
    private scrollService:ScrollService,
    private reportGridService:ReportGridService,
    private reportDataService:ReportDataService,
    private reportFiltersUtil:ReportFiltersUtil,
    private reportActionsService:ReportActionsService,
    private stateService:StateService,
    private transition:Transition,
    private reportExportService:ReportExportService,
    private messageService:GaMessageService,
    private sendMailService:SendMailService,
    private browseListService:BrowseListService,
    private storedReportDataService:StoredReportDataService,
    private translationService:TranslationService,
    private cdr:ChangeDetectorRef,
    private deferredService:GaDeferredService,
    private reportLayoutSettingsService:ReportLayoutSettingsService,
    @Inject(Window) private window:IExtendedWindow
  ) {
    // define edit cell method to be usable from window object
    // referenced for popup renderer in ng1 report style service
    window.editCellFn = this.editCell;
    this.isACSLUser = this.authService.checkAcslUser();
  }

  public ngOnInit():void {
    if (this.browselistCode) {
      this.browselistConfig = this.browseListConfigService.getConfig(this.browselistCode);
      this.reportId = this.browselistConfig.reportId? `${this.browselistConfig.reportId || ''}` : undefined;
    } else {
      this.browselistConfig = this.authService.checkAcslUser() && this.browseListConfigService.findAutoLoadConfig(Number(this.reportId));
    }

    // do not load report details when browselist code is unknown
    if (this.reportId) {
      // report comment text options
      this.reportCommentScreen = `REPORTS.${this.reportId}`;
      this.reportCommentKey = this.isACSLUser? ACSL_COMMENT_KEY : OTHERS_COMMENT_KEY;
      this.filtersControlModel.showfilters = !!this.showFilters;
      this.filtersControlModel.showcomments = !!this.showComments;
      this.filtersControlModel.hascomments = this.translationService.isTranslatable(`REPORTS.${this.reportId}.${this.reportCommentKey}.TEXT`);

      // stored report from param url is priority than in config
      this.storedReport = this.storedReport || this.browselistConfig?.storedReport;
      this.browselistContext = this.formContext? this.reportFiltersUtil.paramToFilters(this.formContext) : undefined;
      this.reportStarting = true;

      this.bodyClassService.add('report');
      this.scrollService.clear(); //do this to prevent unwanted scrolling for state transitions

      this.loadFiltersFromParams(this.transition.params());

      this.reportGridService.start(<string>this.reportId, this.gridOptions, this.title);

      // only set edit/delete row functions on the grid level, the trigger takes place with report-grid.service at on click event
      this.reportGridService.setEditRowFunction(<string>this.reportId, this.editRow);
      this.reportGridService.setDeleteRowFunction(<string>this.reportId, this.deleteRow);

      // set layout grid code
      this.layoutGridCode = this.browselistCode ? `BROWSELISTS_${this.browselistCode}` : `REPORTS_${this.reportId}`;

      Promise.all([
        this.loadStoredReports(),
        this.reportDataService.loadMetaData(this.reportId),
        this.reportLayoutSettingsService.loadReportLayoutSettings([this.layoutGridCode], this.menuId, this.storedReport?.toString())
      ]).then((results:[ void, IReportMetadata, IReportLayoutSetting[] ]) => {
        return results[ 1 ];
      })
      .then(this.installMetaData)
      .then(this.installLayoutSetting)
      .then(this.autoLoadData)
      .then(this.installData)
      .then(this.initColumnsSizing)
      .finally(() => {
        this.setLoading(false);
      });

      this.listen();
    }
  }

  public ngAfterContentChecked():void {
    // Angular runs change detection and when it finds that some values which has been passed to the child component have been changed, Angular throws the following error: 'ExpressionChangedAfterItHasBeenCheckedError'
    // In order to correct this always detect changes in 'AfterContentChecked' lifecycle hook
    this.cdr.detectChanges();
  }

  public uiOnParamsChanged(newParams:IObject, transition:Transition):void {
    const storedReport:string = newParams?.[ReportParameterCode.STORED_REPORT] || newParams?.[ReportParameterShortCode.STORED_REPORT];
    const formContext:string = newParams?.[ReportParameterCode.FORM_CONTEXT] || newParams?.[ReportParameterShortCode.FORM_CONTEXT];

    // update stored report ID or code
    if (storedReport && this.storedReports) {
      // 'storedReport' param accepts string and number (string for 'code' and number for 'stored report id')
      this.storedReport = util.tryNum(storedReport);
    }

    // update browselist form context (ga3 forms)
    if (formContext) {
      this.browselistContext = this.reportFiltersUtil.paramToFilters(formContext);
    }

    // update filters parameter value if available
    const savedFiltersParamValue:string = this.persistedStateParams[ReportParameterShortCode.FILTERS];
    const extendedNewParams:IObject = _.extend({ ...transition.params() }, newParams);
    const newFiltersParamValue:string = extendedNewParams[ReportParameterShortCode.FILTERS];
    if (!_.isEqual(savedFiltersParamValue, newFiltersParamValue)) { // this happens when users go back or forward
      this.cancelLoad();
      this.loadFiltersFromParams(extendedNewParams);

      this.autoLoadData(this.currentReportMetadata)
        .then(this.installData);
    }
  }

  private listen():void {
    this.getRowCountSubscription = this.reportGridService.getRowCount(<string>this.reportId)
      .subscribe((rowCount:number) => this.rowCount = rowCount);

    // Subscription to handle layout setting changes dynamically for the current grid
    this.onLayoutSettingChangeSubscription = this.reportGridService.onLayoutSettingChange
      .subscribe(():void => {
        if (this.resetLayoutSettingsBusy) { return; }
        
        const layoutSetting:IReportLayoutSetting = this.reportLayoutSettingsService.updateLayoutSettingsOnChange(
          <number>this.reportId,
          this.layoutGridCode,
          this.selectedLayoutSetting,
          this.currentReportMetadata,
          this.backupCurrentReportMetadata || this.originalReportMetadata,
          this.originalStoredReportMetadata,
          this.menuId,
          this.storedReport?.toString(),
          this.tableSettingsModel
        );

        // update current selected layout setting
        this.updateCurrentLayoutSetting(layoutSetting);
        
        // update report table columns checkboxes
        this.updateReportColumnsList(this.currentReportMetadata);
      });

    // Subscription to handle the "save" event for layout settings
    // We need to make "layoutSetting" object up-to-date from server (because it might contain 'id')
    this.onLayoutSettingSavedSubscription = this.reportLayoutSettingsService.onSaved.subscribe(() => {
      this.selectedLayoutSetting = this.getCurrentLayoutSetting();
    });
  }

  public onFilterChange(filterValue:IObject):void {
    this.filters = util.copyAndExtend(this.filters, filterValue);

    this.ga1ReportUrl = this.reportDataService.generateGa1ReportUrl(
      <string>this.reportId,
      this.title,
      !!(this.currentReportMetadata?.filterGroups),
      this.autoload,
      this.filtersControlModel.showfilters,
      this.filters);
  }

  private loadFiltersFromParams(stateParams:IObject):void {
    this.persistedStateParams = _.clone(stateParams);
    this.processedFilterValues = this.reportFiltersUtil.paramToFilters(stateParams[ReportParameterCode.FILTERS], stateParams[ReportParameterShortCode.FILTERS]);
    this.filters = util.copyAndExtend(this.defaultFilters, this.processedFilterValues);
  }

  private installMetaData = async(metadata:IReportMetadata):Promise<void | IReportMetadata> => {
    this.currentReportMetadata = metadata;
    this.originalReportMetadata = util.deepClone(metadata);
    this.title = this.title || metadata.title;
    if (util.notNullOrUndefinedOrEmpty(metadata.comments)) {
      this.comments = metadata.comments;
    }

    // mark as initialized
    this.gridInit = true;

    // set permissions
    this.browselistActionPermissions.add = metadata.permissions?.insertAllowed && !this.browselistConfig?.noAdd;
    this.browselistActionPermissions.edit = metadata.permissions?.updateAllowed && !this.browselistConfig?.noEdit;
    this.browselistActionPermissions.delete = metadata.permissions?.deleteAllowed && !this.browselistConfig?.noDelete;

    // initialize report columns default visibility
    this.updateReportColumnsList(metadata);

    // set default filters and displayed filters on screen value
    this.defaultFilters = _.extend(this.defaultFilters, this.reportFiltersUtil.getDefaultFilters(metadata.columns));

    this.filters = util.copyAndExtend(this.storedReport? {} : this.defaultFilters, this.filters);

    // check if metadata has report actions
    this.actionModel.actions = metadata.actions;
    this.hasCustomActions = !!(metadata.actions && metadata.actions.length);

    // install aggregate from server
    this.installAggregatesFromMetadata(metadata);

    return this.installGridMetadata(metadata)
      .then(this.preSelectStoredReport)
      .then(() => {
        this.reportStarting = false;
        return metadata;
      });
  };
  
  private installGridMetadata(metadata:IReportMetadata):Promise<GridApi> {
    return this.reportGridService.install(<string>this.reportId, metadata, this.hasCustomActions, this.title, this.browselistConfig, this.browselistActionPermissions);
  }

  private installAggregatesFromMetadata(metadata:IReportMetadata):void {
    this.tableSettingsModel = {
      ...this.tableSettingsModel,
      sum: metadata.aggregate?.sum,
      average: metadata.aggregate?.average,
      count: metadata.aggregate?.count,
      min: metadata.aggregate?.min,
      max: metadata.aggregate?.max
    };
  }

  private installSubColumns(subColumns:any):void {
    this.reportGridService.installSubColumns(<string>this.reportId, subColumns, this.browselistActionPermissions);
  }

  // this will start a load of data when 'autoload' setting is defined
  private autoLoadData = (metadata:IReportMetadata):Promise<IReportData | string> => {
    if (metadata.autoLoad || this.autoload) {
      return new Promise((resolve, reject) => {
        // wait for the filters to be set to the form before loading the data
        setTimeout(() => {
          this.loadData()
            .then(resolve)
            .catch(reject);
        });
      });
    } else {
      return Promise.resolve(ReportDataService.EMPTY_MARKER);
    }
  };

  private loadData = async():Promise<IReportData | string> => {
    this.setLoading(true);
    this.scrollToResults();

    const queryFilters:IObject = this.getQueryFilters();

    return this.reportDataService.loadData(<string>this.reportId, this.reportId, queryFilters, _.pick(this.tableSettingsModel, aggregateModels), this.currentReportMetadata.columns)
      .then((response:IReportResponse) => {
        if (response.subColumns) {
          this.installSubColumns(response.subColumns);
        }
        this.sqlModel = response.data.sql;
        return response.data;
      })
      .catch((rejection) => {
        if (rejection && _.isObject(rejection)) {
          this.sqlModel = rejection.sql;
        }
        return ReportDataService.EMPTY_MARKER;
      }) // this can also happen when the load is cancelled
      .finally(() => this.setLoading(false));
  };

  private installData = (data:IReportData | string):any => {
    return this.reportGridService.setData(<string>this.reportId, data);
  };

  private getQueryFilters():any {
    return this.reportFiltersUtil.getQueryFiltersFromModel(this.filters, this.currentReportMetadata, {
      applyDefaultValues: false,
      applyNullValues: true
    });
  }

  public load = async():Promise<IReportData | string> => {
    this.saveFiltersStateParams();
    return this.loadData()
      .then(this.installData);
  };

  public executeAction(selectedAction:IReportAction):void {
    this.reportActionBusy = true;
    this.reportActionsService.executeAction(Number(this.reportId), selectedAction)
      .then((actionExecuted:boolean) => {
        if (actionExecuted && this.reportActionsService.shouldReloadRows(selectedAction) && !_.isEmpty(this.processedFilterValues)) {
          this.load();
        }
      })
      .finally(() => {
        this.reportActionBusy = false;
      });
  }

  public cancelLoad():void {
    this.reportDataService.cancelLoadData(<string>this.reportId);
  }

  private setLoading(isLoading:boolean):void {
    this.reportDataLoading = isLoading;
    this.reportGridService.setLoading(<string>this.reportId, isLoading);
  }

  public exportReport = ():void => {
    this.reportExportService.exportReport(Number(this.reportId), this.currentReportMetadata, this.getQueryFilters(), this.title, this.overwriteExportCheckboxes, this.templateCode);
  };

  public sendReport = ():void => {
    const queryFilters = this.getQueryFilters();

    const onSendMailFn:(model:IMailRequest) => any = async(model:IMailRequest):Promise<any> => {
      // the report sent by email contains only columns that are visible in the grid
      const columns:string[] = [];
      if (this.overwriteExportCheckboxes) {
        _.each(this.currentReportMetadata.columns, (column:IColumnMetaData) => {
          if (column.formatting && column.formatting.defaultinlist) {
            columns.push(column.name);
          }
        });
      }
      return this.reportDataService.sendReport(model, this.reportId, queryFilters, _.pick(this.tableSettingsModel, aggregateModels), columns)
        .then(() => {
          this.messageService.showFeedbackMessage(`The report ${this.title} has been sent`);
        });
    };

    const onCancelFn:() => void = ():void => {
      this.reportDataService.cancelSendMailData(this.reportId);
    };

    this.sendMailService.showSendMailDialog('Send Report', `ACSL Report - ${this.title}`, undefined, onSendMailFn, onCancelFn);
  };

  public addRow():void {
    // save the state filter to obtain their actual state
    this.saveFiltersStateParams()
      .then(() => {
        this.browseListService.addRow(<string>this.reportId, this.browselistConfig, this.getQueryFilters(), this.browselistContext, this.load, this.title);
      });
  }

  public editRow = (visibleIndex:number, editBtnElement:HTMLInputElement):void => {
    this.browseListService.editRow(<string>this.reportId, this.browselistConfig, visibleIndex, this.getQueryFilters(), this.browselistContext, editBtnElement, this.title, this.load);
  };

  public editCell = (visibleIndex:number, config:IBrowseListConfig):Promise<string | IReportRow> => {
    return this.browseListService.editRow(<string>this.reportId, config, visibleIndex, this.getQueryFilters(), this.browselistContext, undefined, this.title, this.load);
  };

  public deleteRow = (rowIndex:number, title:string):void => {
    // save the state filter to obtain their actual state
    this.saveFiltersStateParams()
      .then(() => {
        this.browselistDeleteBusy = true;
        this.browseListService.deleteRow(<string>this.reportId, this.browselistConfig, this.getQueryFilters(), this.load, rowIndex, this.browselistContext, title)
          .finally(() => {
            this.browselistDeleteBusy = false;
          });
      });
  };

  /**
   * This function manages and applies report layout settings, either by decoding them
   * from a query parameter token or retrieving them from the database. If a layout setting
   * is provided from the token, it validates the owner of the setting and enforces certain
   * configurations (e.g., resetting the display type for non-owner users). When the owner is
   * the current user, the layout settings are retrieved from the server or local storage.
   *
   * The layout settings are then applied to update the report's metadata and configure
   * the corresponding report grid.
   *
   * @returns {IReportMetadata} The updated report metadata after applying the layout settings.
   */
  private installLayoutSetting = ():IReportMetadata => {
    /**
     * Decodes layout settings from query parameter if any and applies them.
     * If token is invalid or not exist then try load layout setting from the database.
     */
    let providedLayoutSetting:IReportLayoutSetting;
    if (this.layoutSettingToken) {
      // Decode the layout settings from the token
      providedLayoutSetting = this.reportLayoutSettingsService.decodeLayoutSetting(this.layoutSettingToken);

      // when provided layout setting is not yourself as the owner then always reset display type to normal view
      if (!_.isEqual(providedLayoutSetting.ownerId, this.authService.getCurrentUser()?.id)) {
        providedLayoutSetting.displayType = ReportDisplayType.NORMAL;
        // add or update layout setting
        this.reportLayoutSettingsService.addOrUpdateLayoutSetting(providedLayoutSetting);
      } else {
        // whenever user is the owner of layout setting, should get it from server/localStorage
        providedLayoutSetting = undefined;
      }
    }

    const layoutSetting:IReportLayoutSetting = providedLayoutSetting || this.getCurrentLayoutSetting();
    if (layoutSetting) {
      this.applyOrFetchLayoutSetting(layoutSetting);
      this.reportLayoutSettingsService.updateColumnMetadata(this.currentReportMetadata, layoutSetting);
      this.backupCurrentReportMetadata = util.deepClone(this.currentReportMetadata);
      this.installGridMetadata(this.currentReportMetadata);
    }

    return this.currentReportMetadata;
  };

  /**
   * Applies the given layout setting to update the table settings model based on the provided or fetched layout setting.
   * If no layout setting is specified, it attempts to fetch the layout settings from the server.
   *
   * @param {IReportLayoutSetting} providedLayoutSetting - The layout setting to apply.
   * @return {Promise<IReportLayoutSetting | void>} A promise that resolves to the applied layout setting or void if no settings are applied.
   */
  private applyOrFetchLayoutSetting(providedLayoutSetting:IReportLayoutSetting):Promise<IReportLayoutSetting> {
    let layoutSettingPromise:Promise<IReportLayoutSetting | void>;
    
    if (!providedLayoutSetting) {
      // we will try to fetch the layout settings with selected parameters (stored report might be changed)
      layoutSettingPromise = this.reportLayoutSettingsService.loadReportLayoutSettings([this.layoutGridCode], this.menuId, this.storedReport?.toString())
        .then(this.getCurrentLayoutSetting);
    } else {
      // do nothing because 
      layoutSettingPromise = Promise.resolve(providedLayoutSetting);
    }
    
    return layoutSettingPromise
      .then((layoutSetting:IReportLayoutSetting) => {
        if (layoutSetting) {
          // update table settings model
          this.tableSettingsModel = {
            ...this.tableSettingsModel,
            display: layoutSetting.displayType,
            columnSizing: _.findWhere(this.reportGridService.sizingOptions, { id: layoutSetting.columnSizing }),
            sum: layoutSetting.showSum,
            average: layoutSetting.showAverage,
            min: layoutSetting.showMinimum,
            max: layoutSetting.showMaximum,
            count: layoutSetting.showCount
          };

          this.selectedLayoutSetting = layoutSetting; 
        }
        
        return layoutSetting;
      });
  }

  /**
   * Resets the current layout settings to their default values.
   *
   * @param {OnResetLayoutSettingsDoneCallback} done - signaling back to the caller component that the reset process is done
   * @return {void} Does not return a value.
   */
  public onResetLayoutSettings(done:OnResetLayoutSettingsDoneCallback):void {
    const currentLayoutSetting:IReportLayoutSetting = this.getCurrentLayoutSetting();
    const deletePromise:Promise<void> = currentLayoutSetting ? this.reportLayoutSettingsService.deleteLayoutSettings(currentLayoutSetting) : Promise.resolve();

    this.resetLayoutSettingsBusy = true;
    deletePromise
      .then(() => {
        // revert table settings model to default
        this.tableSettingsModel = this.defaultTableSettingsModel;
        // update current layout setting
        this.updateCurrentLayoutSetting(undefined);
        this.backupCurrentReportMetadata = util.deepClone(this.originalReportMetadata);
        this.reportGridService.resetGridState(this.reportId.toString());

        // reset metadata to original setting
        return this.installMetaData(this.originalReportMetadata);
      })
      .finally(() => {
        // add timeout until restoring grid state process has been completed (including the timeout process from the grid) 
        setTimeout(() => {
          this.resetLayoutSettingsBusy = false;
          done();
        });
      });
  }

  /**
   * Retrieves the current layout setting based on the provided parameters including grid code, menu ID,
   * stored report ID, and owner ID. It interacts with the report layout settings service to fetch the relevant data.
   *
   * @return {IReportLayoutSetting} The current layout setting object containing the configuration details.
   */
  private getCurrentLayoutSetting = ():IReportLayoutSetting => {
    return this.reportLayoutSettingsService.findLayoutSetting({ gridCode: this.layoutGridCode, menuId: this.menuId, storedReportId: this.storedReport?.toString(), ownerId: this.authService.getCurrentUser()?.id });
  };
  
  /**
   * Updates the current layout setting by assigning the provided layout setting to the selected layout property
   * and updating the URL state parameter accordingly.
   *
   * @param {IReportLayoutSetting} layoutSetting The layout setting to be assigned as the current setting.
   * @return {void}
   */
  private updateCurrentLayoutSetting(layoutSetting:IReportLayoutSetting):void {
    // update current selected layout setting
    this.selectedLayoutSetting = layoutSetting;

    // update url state parameter
    this.layoutSettingToken = this.reportLayoutSettingsService.encodeLayoutSetting(layoutSetting);
    this.updateStateParams({
      [ReportParameterShortCode.LAYOUT_SETTING]: this.layoutSettingToken
    });
  }

  /**
   * Handles the selection of a stored report and updates the state of the application based on the provided stored report.
   *
   * Depending on the provided parameter, this method updates filters, layout settings, and report columns. It ensures
   * that the stored report settings are applied prioritizing the `code` property over the `id` property, since `id`
   * may not be unique.
   *
   * The function performs the following actions:
   * - Updates the `storedReport` and checks if overwriting export checkboxes is required.
   * - Converts the stored report's filters to active filters for the current report.
   * - Resolves conflicts between stored report filters and URL filters (if allowed) with a timeout for proper ordering.
   * - Updates aggregate table settings (e.g., average, count, min, max, sum) based on stored report properties.
   * - Applies layout settings from the stored report.
   * - Reverts filters and layout settings to defaults if the provided stored report is empty.
   * - Updates the layout setting and stored report state parameter, and refreshes the report column list.
   *
   * Returns a Promise that resolves when the function completes all updates and actions.
   *
   * @param {IStoredReportChangeEvent} [param] - An optional parameter containing the stored report details and settings.
   *                                              Includes `storedReport` and options for overwriting filters.
   * @returns {Promise<any>} - A Promise that resolves when all updates related to the stored report are complete.
   */
  public selectStoredReport = (param?:IStoredReportChangeEvent):Promise<any> => {
    const deferred:IGADeferred<void> = this.deferredService.defer();

    // 'code' is priority than 'id' because 'id' is not unique
    this.storedReport = param?.storedReport?.code || param?.storedReport?.id;
    this.overwriteExportCheckboxes = !!this.storedReport;

    if (param?.storedReport) {
      const storedReportFilters = this.reportFiltersUtil.convertStoredReportToFilters(this.currentReportMetadata, param.storedReport);
      // when the url param storedReport matches selectedStoreReport.id, the storedReport filters can be overwritten by the url filters
      // add timeout to make stored report filters always applied last
      setTimeout(() => {
        this.filters = param.allowOverwriteByFilterStateParam? util.copyAndExtend(storedReportFilters, util.deepCloneWithSanitization(this.processedFilterValues)) : storedReportFilters;
        deferred.resolve();
      });

      // load the aggregate state from the storedReport
      this.tableSettingsModel = {
        ...this.tableSettingsModel,
        average: _.isUndefined(param.storedReport.showAverage)? this.tableSettingsModel.average : param.storedReport.showAverage,
        count: _.isUndefined(param.storedReport.showCount)? this.tableSettingsModel.count : param.storedReport.showCount,
        max: _.isUndefined(param.storedReport.showMaximum)? this.tableSettingsModel.max : param.storedReport.showMaximum,
        min: _.isUndefined(param.storedReport.showMinimum)? this.tableSettingsModel.min : param.storedReport.showMinimum,
        sum: _.isUndefined(param.storedReport.showSum)? this.tableSettingsModel.sum : param.storedReport.showSum
      };

      // apply stored report layout (combination with layout settings)
      this.applyStoredReportLayout(param.storedReport, true);
    } else {
      // revert back filters to default when select empty list
      this.filters = util.copyAndExtend({}, this.defaultFilters);
      this.saveFiltersStateParams(true);

      // restore layout settings to original report metadata or combination with layout settings (if any)
      this.applyStoredReportLayout({ columns: this.reportFiltersUtil.convertFiltersToStoredReport(this.originalReportMetadata, this.filters) })
        .then(deferred.resolve);
    }

    this.selectedLayoutSetting = this.getCurrentLayoutSetting();
    this.saveStoredReportStateParams(this.storedReport);
    this.updateReportColumnsList(this.currentReportMetadata);

    return deferred.promise;
  };

  /**
   * Applies the stored report layout configuration to the current report and updates metadata accordingly.
   *
   * @param {IStoredReport} storedReport - The stored report instance containing layout configurations to be applied.
   * @param {boolean} [isBackup] - Optional. If true, the current report metadata will be backed up before applying the layout.
   * @return {Promise<any>} A promise that resolves when the stored report layout has been successfully applied.
   */
  private async applyStoredReportLayout(storedReport:IStoredReport, isBackup?:boolean):Promise<any> {
    const currentLayoutSetting:IReportLayoutSetting = await this.applyOrFetchLayoutSetting(this.getCurrentLayoutSetting());

    // if layout settings exist with current stored report selection then apply layout setting
    // otherwise apply the provided stored report
    if (currentLayoutSetting) {
      this.reportLayoutSettingsService.updateColumnMetadata(this.currentReportMetadata, currentLayoutSetting);
    } else {
      this.reportFiltersUtil.updateColumnMetadata(this.currentReportMetadata, storedReport);
    }

    if (isBackup) {
      // when doing backup of stored report metadata we should store it with original report metadata + stored report configuration
      this.originalStoredReportMetadata = util.deepClone(this.originalReportMetadata);
      this.reportFiltersUtil.updateColumnMetadata(this.originalStoredReportMetadata, storedReport);
    }

    this.reportGridService.setStoredReportBusy(true);
    return this.installGridMetadata(this.currentReportMetadata)
      .finally(() => {
        // add timeout to let ag-grid has been fully rendered with stored report settings
        setTimeout(() => {
          this.reportGridService.setStoredReportBusy(false);
        });
      });
  }

  public saveStoredReport = async(param:{ reportId:number, storedReportId:number, storedReport:IStoredReport }):Promise<IStoredReport | void> => {
    //store the current aggregate state in the storedReport
    param.storedReport.showAverage = this.tableSettingsModel.average;
    param.storedReport.showCount = this.tableSettingsModel.count;
    param.storedReport.showMaximum = this.tableSettingsModel.max;
    param.storedReport.showMinimum = this.tableSettingsModel.min;
    param.storedReport.showSum = this.tableSettingsModel.sum;

    // store the current aggregate column value and grouping sequence
    _.forEach(param.storedReport.columns, (column:IStoredReportColumn) => {
      const colMetadata = _.find(this.currentReportMetadata.columns, (meta:IColumnMetaData) => meta.name === column.name);
      column.groupingSequence = colMetadata.groupingSequence;
      column.aggregateOn = colMetadata.aggregateOn;
    });

    this.storedReportsState.saving = true;
    if (param.storedReportId) {
      return this.storedReportDataService.saveExistingStoredReport(param.reportId, param.storedReportId, param.storedReport)
        .then((storedReport:IStoredReport) => {
          const storedReportIndex = _.findIndex(this.storedReports, (sr:IStoredReport) => {
            return sr.id === storedReport?.id;
          });
          this.storedReports[ storedReportIndex ] = storedReport;
          this.selectStoredReport({ storedReport });
        })
        .finally(() => this.storedReportsState.saving = false);
    } else {
      param.storedReport.browselistCode = this.browselistCode;

      return this.storedReportDataService.saveNewStoredReport(param.reportId, param.storedReport)
        .then((storedReport:IStoredReport) => {
          this.storedReports = this.storedReports.concat([ storedReport ]);
          this.selectStoredReport({ storedReport });
        })
        .finally(() => this.storedReportsState.saving = false);
    }
  };

  public deleteStoredReport = async(param:{ reportId:number, storedReportId:number }):Promise<any> => {
    this.storedReportsState.deleting = true;
    return this.storedReportDataService.deleteStoredReport(param.reportId, param.storedReportId)
      .then(() => {
        this.storedReport = this.storedReport === param.storedReportId? undefined : this.storedReport;

        this.selectStoredReport()
          .then(() => {
            this.storedReports = _.reject(this.storedReports, (storedReport:IStoredReport) => {
              return storedReport.id === param.storedReportId;
            });
          });
        this.storedReportsState.deleting = false;
      })
      .finally(() => this.storedReportsState.deleting = false);
  };

  public onStoredReportDialogStateChange(param:IStoredReportDialogStateChangeEvent):void {
    // improve performance when stored report dialog open, then we hide report filters the one not in dialog
    this.showReportFiltersWhenDialogOpen = !param.isOpen;

    if (util.notNullOrUndefinedOrEmpty(param.filters)) {
      this.onFilterChange(param.filters);
    }
  }

  private loadStoredReports = async():Promise<void> => {
    this.storedReportsState.loading = true;
    return this.storedReportDataService.loadStoredReports(this.reportId)
      .then((reports:IStoredReport[]) => {
        // we need to set 'storedReportId' here so when <stored-report> component load initially, input binding 'storedReportId' -> first change already contain value from state params
        this.storedReport = _.clone(this.storedReport);
        this.storedReports = reports;
        this.storedReportsState.loading = false;
      });
  };

  private preSelectStoredReport = ():Promise<void> => {
    if (this.storedReport) {
      // search report by report id / report code
      const storedReport:IStoredReport = _.find(this.storedReports, (storedReport:IStoredReport) => storedReport.id === Number(this.storedReport) || storedReport.code === this.storedReport);

      if (!storedReport) { //not in the list of the users stored reports => load it from the server
        return this.storedReportDataService.loadStoredReport(this.reportId, this.storedReport)
          .then((storedReport:IStoredReport) => {
            if (storedReport) {
              // user can select this extra loaded stored report but cannot edit
              // while dialog open this option would be disabled
              this.storedReports = this.storedReports.concat([{
                ...storedReport,
                editDisabled: true
              }]);

              return this.selectStoredReport({ storedReport, allowOverwriteByFilterStateParam: true });
            } else {
              // clear 'storedReport' param when it doesn't give any result(s)
              this.saveStoredReportStateParams();
              return Promise.resolve();
            }
          });
      } else {
        return this.selectStoredReport({ storedReport, allowOverwriteByFilterStateParam: true });
      }
    }
    return Promise.resolve();
  };

  /**
   * Updates the persisted state parameters and optionally handles redirection.
   *
   * @param {IObject} params - The parameters to be merged into the persisted state parameters.
   * @param {boolean} [redirect=true] - Determines whether to redirect after updating the parameters. Default is true.
   * @return {Promise<void | StateObject>} A promise that resolves to void if no redirection occurs, or a StateObject if redirection is handled.
   */
  private updateStateParams(params:IObject, redirect:boolean = true):Promise<void | StateObject> {
    // Update persistedStateParams
    this.persistedStateParams = { ...this.persistedStateParams, ...params };

    // Handle redirection if needed
    return redirect? this.stateService.go(this.stateService.current.name, this.persistedStateParams, { location: 'replace' }) : Promise.resolve();
  }

  /**
   * Saves the state parameters for a stored report by using a given stored report ID.
   *
   * @param {string | number} [storedReportId] - The ID of the stored report. It can be a string or a number. If undefined, the parameter will be cleared.
   * @return {void} This method does not return a value.
   */
  private saveStoredReportStateParams(storedReportId?:string | number):void {
    const params:IObject = {
      [ReportParameterCode.STORED_REPORT]: undefined, // Clear long parameter name
      [ReportParameterShortCode.STORED_REPORT]: storedReportId // Set short parameter name
    };

    this.updateStateParams(params); // Use generic method for consistent behavior
  }

  /**
   * Saves the current state of the filter parameters if they have changed.
   *
   * @param {boolean} [noRedirection=false] - Indicates whether to skip redirection after updating the state parameters.
   * @return {Promise<void | StateObject>} A promise that resolves when the state parameters are updated. Returns the state object if updated, otherwise resolves with no value if there are no changes.
   */
  private saveFiltersStateParams(noRedirection:boolean = false):Promise<void | StateObject> {
    const currentProcessedFilterValues:IObject = this.reportFiltersUtil.processNonDefaultFilters(
      this.filters,
      this.defaultFilters
    );

    // Update only if the filters have changed
    if (!_.isEqual(this.processedFilterValues, currentProcessedFilterValues)) {
      this.processedFilterValues = currentProcessedFilterValues;

      const params:IObject = {
        [ReportParameterCode.FILTERS]: undefined, // Clear long parameter name
        [ReportParameterShortCode.FILTERS]: this.reportFiltersUtil.filtersToParam(this.filters, this.defaultFilters) // Set short parameter name
      };

      return this.updateStateParams(params, !noRedirection); // Use generic method
    }

    return Promise.resolve(); // No changes, do nothing
  }

  private initColumnsSizing = ():void => {
    this.reportGridService.setColumnSizing(<string>this.reportId, this.tableSettingsModel.columnSizing);
  };

  public onColumnSelection = ():void => {
    this.reportGridService.updateColumnsVisibility(<string>this.reportId);
    this.overwriteExportCheckboxes = true;
  };

  private updateReportColumnsList(metadata:IReportMetadata):void {
    this.columnControlModel.possibleColumns = [];
    _.chain(metadata?.columns)
      .sortBy((col:IColumnMetaData) => col.name)
      .sortBy((col:IColumnMetaData) => col.listSequence)
      .forEach((col:IColumnMetaData) => {
        if (col.formatting && col.formatting.showinlist) {
          this.columnControlModel.possibleColumns.push(col);
        }
      });
  }

  private scrollToResults():void {
    this.scrollService.scrollTo('report-results');
  }
  
  private get defaultTableSettingsModel():IReportTableSettingsModel {
    return {
      columnSizing: ReportGridService.SIZING_HEADER,
      sum: false,
      count: false,
      average: false,
      min: false,
      max: false,
      display: ReportDisplayType.NORMAL
    };
  }

  public ngOnDestroy():void {
    // remove editCellFn window object
    delete this.window.editCellFn;
    this.cancelLoad();
    this.reportGridService.stop();
    this.bodyClassService.remove('report');
    this.reportDataService.clear();
    this.getRowCountSubscription?.unsubscribe();
    this.onLayoutSettingChangeSubscription?.unsubscribe();
    this.onLayoutSettingSavedSubscription?.unsubscribe();
  }
}